"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { CarouselControls } from "@/features/shared/components/carousel-controls";

interface WebsiteTemplate {
  id: string;
  nome: string;
  categoria: string;
  imagemUrl: string;
  previewUrl: string;
}

const TEMPLATES: WebsiteTemplate[] = [

  {
    id: "1",
    nome: "Canopy Stilt Lodge",
    categoria: "Exemplo Site 1",
    imagemUrl: "/assets/imgs/site-hoteleiro/modelos/modelo1.webp",
    previewUrl: "https://modelo1.soufoco.com",
  },
  {
    id: "2",
    nome: "Mata Refúgio Lodge e Selva",
    categoria: "Exemplo Site 2",
    imagemUrl: "/assets/imgs/site-hoteleiro/modelos/modelo2.webp",
    previewUrl: "https://modelo2.soufoco.com",
  },
  {
    id: "3",
    nome: "Mirror Lake Retreat",
    categoria: "Exemplo Site 3",
    imagemUrl: "/assets/imgs/site-hoteleiro/modelos/modelo3.webp",
    previewUrl: "https://modelo3.soufoco.com",
  },
  {
    id: "4",
    nome: "Rede Terra e Selva Collection",
    categoria: "Exemplo Portal de Rede",
    imagemUrl: "/assets/imgs/site-hoteleiro/modelos/modelo-rede.webp",
    previewUrl: "https://rede.soufoco.com",
  },
];

function WebsitePortfolioCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  // Autoplay do Embla pausa fora da viewport — mesmo padrão já usado no
  // WallOfLoveSection/ExperienciasSection/RecursosGridSection: sem isso, o
  // plugin Autoplay do embla-carousel roda pra sempre em background (timer +
  // scroll animado a cada 4s), mesmo com o carrossel rolado bem longe da tela.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = api?.plugins()?.autoplay;
    if (!autoplay) return;
    if (isVisible) autoplay.play();
    else autoplay.stop();
  }, [api, isVisible]);

  const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);

  return (
    // O PNG de fundo saiu: a superfície é #f4f7fb e os modelos de site
    // passam a ser as únicas peças com imagem — sem competição visual.
    <section ref={sectionRef} className="py-20 relative flex overflow-hidden bg-[#f4f7fb]">
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-5xl font-semibold text-[#132840] leading-none tracking-tighter antialiased mb-4">
            Sites de alta performance para{" "}
            <span className="bg-gradient-to-r from-[#285992] via-[#427ab9] to-[#285992] bg-clip-text text-transparent">vendas diretas</span>
          </h2>
          <p className="text-[#4c5c73] mt-4 max-w-3xl mx-auto">
            Escolha o modelo de sua preferência ou personalize seu site e potencialize sua lucratividade com uma ferramenta desenhada para converter visitantes em hóspedes.
          </p>
        </div>

        {/* Carousel */}
        <Carousel
          setApi={setApi}
          opts={{ align: "start", loop: true }}
          plugins={[
            Autoplay({
              delay: 4000,
              stopOnInteraction: false,
              stopOnMouseEnter: true,
            }),
          ]}
          className="w-full"
        >
          {/* O viewport do Embla PRECISA de overflow-x:hidden (esconder slides
              fora da faixa) — mas misturar overflow-x:hidden com overflow-y:
              visible não funciona como parece: a spec recalcula o eixo
              "visible" para "auto", e overflow:auto ainda CORTA sombra (só
              não mostra scrollbar quando não há conteúdo de layout
              transbordando). Por isso a sombra continuava cortada mesmo com
              aquele ajuste. A correção real: dar respiro vertical DENTRO de
              cada slide (pt-4 pb-8 no wrapper abaixo), para a sombra nunca
              precisar sair da própria caixa do slide — sem depender de
              relaxar o overflow do viewport compartilhado. */}
          <CarouselContent className="-ml-4">
            {TEMPLATES.map((template) => (
              <CarouselItem
                key={template.id}
                className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
              >
                {/* overflow-hidden vive no wrapper da imagem, não no card: este
                    div de fora carrega a sombra (.paper/.paper-hover) e o
                    translateY do hover, e um overflow-hidden na MESMA caixa
                    que desenha a sombra cortaria a própria sombra. */}
                <div className="pt-4 pb-8">
                <div className="group relative rounded-3xl paper paper-hover">

                  {/* aspect-[815/584] = a proporção real dos screenshots (mesma
                      usada nos mockups de hero do site) — antes forçava 16/10,
                      cortando as bordas do print em vez de mostrar a página inteira. */}
                  <div className="relative overflow-hidden rounded-t-3xl aspect-[815/584]">
                    <img
                      src={template.imagemUrl}
                      alt={template.nome}
                      width={815}
                      height={584}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-[#1e3a5f]/0 group-hover:bg-[#1e3a5f]/55 transition-colors duration-300 flex items-center justify-center">
                      <a
                        href={template.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-2 bg-white text-[#1e3a5f] text-sm font-semibold px-5 py-2.5 rounded-full shadow-lg hover:bg-blue-50"
                      >
                        <ExternalLink className="w-4 h-4" strokeWidth={2} />
                        Ver modelo do site
                      </a>
                    </div>
                  </div>

                  <div className="p-4">
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-blue-500">
                      {template.categoria}
                    </span>
                  </div>
                </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="flex justify-center mt-8">
          <CarouselControls
            count={count}
            current={current}
            onPrev={() => api?.scrollPrev()}
            onNext={() => api?.scrollNext()}
            onSelect={scrollTo}
            labelPrev="Slide anterior"
            labelNext="Próximo slide"
          />
        </div>

      </div>
    </section>
  );
}

export { WebsitePortfolioCarousel };
